import Logger from './logger.js';

const ROLES = {
  Admin: 'Admin',
  Scheduler: 'Scheduler',
  Maintenance: 'Maintenance',
  Viewer: 'Viewer'
};

const PERMISSIONS = {
  // Schedule permissions
  VIEW_ALL_SCHEDULES: 'view_all_schedules',
  CREATE_SCHEDULES: 'create_schedules',
  EDIT_SCHEDULES: 'edit_schedules',
  DELETE_SCHEDULES: 'delete_schedules',

  // User management
  MANAGE_USERS: 'manage_users',

  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',

  // Fleet management
  MANAGE_FLEET: 'manage_fleet',
  SYNC_MOTIVE_DATA: 'sync_motive_data',

  // Maintenance
  VIEW_ASSIGNED_TASKS: 'view_assigned_tasks',
  UPDATE_TASK_STATUS: 'update_task_status',
  VIEW_OWN_SCHEDULE: 'view_own_schedule',

  // Basic viewing
  VIEW_SCHEDULES: 'view_schedules',
  VIEW_TV_DASHBOARD: 'view_tv_dashboard'
};

const ROLE_PERMISSIONS = {
  [ROLES.Admin]: [
    PERMISSIONS.VIEW_ALL_SCHEDULES,
    PERMISSIONS.CREATE_SCHEDULES,
    PERMISSIONS.EDIT_SCHEDULES,
    PERMISSIONS.DELETE_SCHEDULES,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.MANAGE_FLEET,
    PERMISSIONS.SYNC_MOTIVE_DATA,
    PERMISSIONS.VIEW_TV_DASHBOARD
  ],
  // Map RBC-Fleet-Administrators group ID to Admin permissions
  'e8ada2ec-2575-4b5a-b8ed-bd49f4726edc': [
    PERMISSIONS.VIEW_ALL_SCHEDULES,
    PERMISSIONS.CREATE_SCHEDULES,
    PERMISSIONS.EDIT_SCHEDULES,
    PERMISSIONS.DELETE_SCHEDULES,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.MANAGE_FLEET,
    PERMISSIONS.SYNC_MOTIVE_DATA,
    PERMISSIONS.VIEW_TV_DASHBOARD
  ],
  [ROLES.Scheduler]: [
    PERMISSIONS.VIEW_ALL_SCHEDULES,
    PERMISSIONS.CREATE_SCHEDULES,
    PERMISSIONS.EDIT_SCHEDULES,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VIEW_TV_DASHBOARD
  ],
  [ROLES.Maintenance]: [
    PERMISSIONS.VIEW_ASSIGNED_TASKS,
    PERMISSIONS.UPDATE_TASK_STATUS,
    PERMISSIONS.VIEW_OWN_SCHEDULE,
    PERMISSIONS.VIEW_TV_DASHBOARD
  ],
  [ROLES.Viewer]: [PERMISSIONS.VIEW_SCHEDULES, PERMISSIONS.VIEW_REPORTS, PERMISSIONS.VIEW_TV_DASHBOARD]
};

/**
 * Extract roles from JWT token
 * @param {string} token - JWT token
 * @returns {string[]} Array of role names
 */
export const getRoleFromToken = token => {
  if (!token) return [];

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const roles = payload.roles || [];

    // Also check for custom claims
    const customRoles = payload['extension_roles'] || payload['groups'] || [];

    // The automated E2E service principal (RBC-Fleet-E2E-Testing) carries the app-only `e2e_test`
    // role and no user groups. Treat it as `admin` so the E2E session can reach every role-gated
    // page — mirrors the backend, which grants the e2e_test role full access. Only that SP can hold
    // this role (allowedMemberTypes: ["Application"]).
    return [...new Set([...roles, ...customRoles])].map(role =>
      String(role).toLowerCase() === 'e2e_test' ? 'admin' : role
    );
  } catch (error) {
    Logger.error('Failed to parse token:', error);
    return [];
  }
};

/**
 * Get permissions for given roles
 * @param {string[]} roles - Array of role names
 * @returns {string[]} Array of permission strings
 */
export const getUserPermissions = roles => {
  const permissions = new Set();

  // Normalize roles to capitalize first letter for consistency
  const normalizedRoles = roles.map(role => {
    if (typeof role === 'string') {
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }
    return role;
  });

  // If user has Admin role, grant all permissions immediately
  if (normalizedRoles.includes('Admin')) {
    Object.values(PERMISSIONS).forEach(permission => permissions.add(permission));
    return Array.from(permissions);
  }

  normalizedRoles.forEach(role => {
    const rolePermissions = ROLE_PERMISSIONS[role] || [];
    rolePermissions.forEach(permission => permissions.add(permission));
  });

  return Array.from(permissions);
};

/**
 * Check if user has required permission
 * @param {string[]} userPermissions - User's permissions
 * @param {string} requiredPermission - Required permission
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the required permissions
 * @param {string[]} userPermissions - User's permissions
 * @param {string[]} requiredPermissions - Array of required permissions
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all required permissions
 * @param {string[]} userPermissions - User's permissions
 * @param {string[]} requiredPermissions - Array of required permissions
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

/**
 * Check if user has a specific role
 * @param {string[]} userRoles - User's roles
 * @param {string} requiredRole - Required role
 * @returns {boolean} True if user has the role
 */
export const hasRole = (userRoles, requiredRole) => {
  return userRoles.includes(requiredRole);
};

/**
 * Check if user has any of the specified roles
 * @param {string[]} userRoles - User's roles
 * @param {string[]} requiredRoles - Required roles
 * @returns {boolean} True if user has at least one role
 */
export const hasAnyRole = (userRoles, requiredRoles) => {
  return requiredRoles.some(role => userRoles.includes(role));
};

// Export constants for use in components
export { ROLES, PERMISSIONS, ROLE_PERMISSIONS };
